import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound } from "@tanstack/react-router"

import { PageContent } from "@/components/common/page-content"
import { PageHeader } from "@/components/common/page-header"
import { ReviewActions } from "@/features/reviews/components/review-actions"
import { ReviewIdentityDocuments } from "@/features/reviews/components/review-identity-documents"
import {
  AskForPartButton,
  ReviewPartChanges,
  ReviewPartRecord,
  ReviewPartStanding,
} from "@/features/reviews/components/review-part"
import { reviewQueries } from "@/queries/reviews"

/**
 * One part of the person's record on their review: where it stands, what
 * changed in it, and how it reads.
 */
export const Route = createFileRoute(
  "/$organisation/_authenticated/_organisation/reviews/$reviewId/parts/$section/"
)({
  loader: async ({ context, params }) => {
    const review = await context.queryClient.ensureQueryData(
      reviewQueries.detail(params.reviewId)
    )

    if (!review.parts.some((part) => part.section === params.section)) {
      throw notFound()
    }
  },
  component: ReviewPartPage,
})

function ReviewPartPage() {
  const { reviewId, section } = Route.useParams()
  const { data: review } = useSuspenseQuery(reviewQueries.detail(reviewId))
  const part = review.parts.find((entry) => entry.section === section)

  if (!part) {
    return null
  }

  return (
    <>
      <PageHeader
        title={part.title}
        actions={
          <>
            <AskForPartButton reviewId={reviewId} part={part} />
            <ReviewActions review={review} />
          </>
        }
      />
      <PageContent>
        <ReviewPartStanding part={part} />
        <ReviewPartChanges part={part} />
        {part.section === "individual.identity" && review.subject ? (
          <ReviewIdentityDocuments
            reviewId={reviewId}
            individualId={review.subject.id}
          />
        ) : (
          <ReviewPartRecord part={part} />
        )}
      </PageContent>
    </>
  )
}
